import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Loader2, Upload, CheckCircle2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Bulk Import Challenges modal.
 * Accepts JSON array, single object, or multiple sequential JSON objects.
 * Dedupes by title on the backend.
 */
export const BulkImportChallengesModal = ({ isOpen, onClose, api, onImportComplete }) => {
  const [raw, setRaw] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const parseInput = (text) => {
    const trimmed = text.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) return parsed;
      if (typeof parsed === 'object' && parsed !== null) return [parsed];
    } catch {
      // fall through
    }
    const objects = [];
    let depth = 0;
    let start = -1;
    for (let i = 0; i < trimmed.length; i++) {
      const ch = trimmed[i];
      if (ch === '{') {
        if (depth === 0) start = i;
        depth++;
      } else if (ch === '}') {
        depth--;
        if (depth === 0 && start !== -1) {
          const chunk = trimmed.slice(start, i + 1);
          try {
            objects.push(JSON.parse(chunk));
          } catch (e) {
            throw new Error(`Failed to parse object at position ${start}: ${e.message}`);
          }
          start = -1;
        }
      }
    }
    return objects;
  };

  const handleImport = async () => {
    let challenges;
    try {
      challenges = parseInput(raw);
    } catch (e) {
      toast.error(`Parse error: ${e.message}`);
      return;
    }
    if (!challenges.length) {
      toast.error('No challenge objects found.');
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const res = await api().post('/admin/challenges/bulk-import', { challenges });
      setResult(res.data);
      toast.success(`Imported ${res.data.inserted_count} / skipped ${res.data.skipped_count} / errors ${res.data.error_count}`);
      if (res.data.inserted_count > 0 && onImportComplete) {
        onImportComplete();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Import failed');
    }
    setLoading(false);
  };

  const handleClose = () => {
    if (loading) return;
    setRaw('');
    setResult(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-2xl bg-card border-border" data-testid="bulk-import-challenges-modal">
        <DialogHeader>
          <DialogTitle className="text-white font-heading uppercase tracking-wide">Bulk Import Challenges</DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Paste a JSON array or multiple JSON challenge records. Duplicates by title are skipped.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <Textarea
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            placeholder={`{\n  "title": "30-Day Push-Up Challenge",\n  "description": "100 push-ups a day for 30 days",\n  "challenge_type": "workout_count",\n  "target_value": 3000,\n  "duration_days": 30,\n  "is_public": true\n}`}
            className="min-h-[260px] font-mono text-xs bg-black border-border text-white"
            disabled={loading}
            data-testid="bulk-import-challenges-textarea"
          />

          {result && (
            <div className="bg-black/50 border border-border rounded p-3 text-sm space-y-2">
              <div className="flex items-center gap-2 text-green-400">
                <CheckCircle2 className="h-4 w-4" />
                <span>Inserted: <strong>{result.inserted_count}</strong></span>
              </div>
              {result.inserted.length > 0 && (
                <ul className="text-xs list-disc list-inside text-muted-foreground ml-5">
                  {result.inserted.slice(0, 10).map((t) => <li key={`ins-${t}`}>{t}</li>)}
                </ul>
              )}
              <div className="text-muted-foreground">
                Skipped: <strong>{result.skipped_count}</strong>
              </div>
              {result.error_count > 0 && (
                <div className="flex items-start gap-2 text-red-400">
                  <AlertTriangle className="h-4 w-4 mt-0.5" />
                  <div>
                    <div>Errors: <strong>{result.error_count}</strong></div>
                    <ul className="mt-1 text-xs list-disc list-inside">
                      {result.errors.slice(0, 5).map((e) => (
                        <li key={`err-${e.title || e.index}-${e.error?.slice(0, 16)}`}>{e.title || `row ${e.index}`}: {e.error}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
              <div className="text-muted-foreground text-xs">
                Total challenges in DB: {result.total_challenges_in_db}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button onClick={handleClose} variant="outline" disabled={loading} data-testid="bulk-import-challenges-cancel-btn">
              Close
            </Button>
            <Button
              onClick={handleImport}
              disabled={loading || !raw.trim()}
              className="bg-primary text-black hover:bg-primary/90"
              data-testid="bulk-import-challenges-submit-btn"
            >
              {loading ? (
                <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Importing...</>
              ) : (
                <><Upload className="h-4 w-4 mr-2" /> Import Challenges</>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
